var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useMemo, useState } from 'react';
import { Button, Checkbox, InlineNotification, Text } from '@ui';
import { createEquipmentTypes } from '../shared/api';
import { CREATE_EQUIPMENT_TYPES_MAX, STANDARD_EQUIPMENT_TYPE_ALIASES, STANDARD_EQUIPMENT_TYPE_NAMES } from '../shared/constants';
import { translatorFor } from '../shared/i18n';
import { errorResponse } from '../shared/types';
const t = translatorFor('setup_results');
const normalize = (name) => name.trim().toLowerCase();
export function MissingEquipmentTypes({ catalog, urls, onEquipmentTypeMapChange, onCreated }) {
    const [selected, setSelected] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [resolved, setResolved] = useState([]);
    const [created, setCreated] = useState([]);
    const [skipped, setSkipped] = useState([]);
    const [failed, setFailed] = useState([]);
    const [discoveryNotApplied, setDiscoveryNotApplied] = useState(false);
    const [error, setError] = useState(null);
    const missing = useMemo(() => {
        const present = new Set([...catalog.map((type) => type.name), ...resolved].map(normalize));
        return STANDARD_EQUIPMENT_TYPE_NAMES.filter((name) => { var _a; return ![name, ...((_a = STANDARD_EQUIPMENT_TYPE_ALIASES[name]) !== null && _a !== void 0 ? _a : [])].some((candidate) => present.has(normalize(candidate))); });
    }, [catalog, resolved]);
    const toggle = (name, checked) => {
        setSelected((previous) => (checked ? [...previous, name] : previous.filter((entry) => entry !== name)));
    };
    const applyResult = (result) => {
        var _a, _b, _c;
        setCreated(result.created);
        setSkipped(result.skipped);
        setFailed(result.failed);
        setResolved((previous) => [...previous, ...result.created, ...result.skipped]);
        setSelected((previous) => previous.filter((name) => result.failed.some((row) => row.name === name)));
        if ((_a = result.discovery) === null || _a === void 0 ? void 0 : _a.equipmentConfig) {
            onEquipmentTypeMapChange((_b = result.discovery.equipmentConfig.equipmentTypeMap) !== null && _b !== void 0 ? _b : []);
        }
        setDiscoveryNotApplied(((_c = result.discovery) === null || _c === void 0 ? void 0 : _c.applied) === false);
    };
    const submit = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        setSubmitting(true);
        setError(null);
        try {
            const result = yield createEquipmentTypes(urls, selected);
            applyResult(result);
            if (result.created.length > 0)
                onCreated === null || onCreated === void 0 ? void 0 : onCreated();
        }
        catch (err) {
            setError((_b = (_a = errorResponse(err)) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : t('create_error'));
        }
        finally {
            setSubmitting(false);
        }
    });
    const hasOutcome = created.length > 0 || skipped.length > 0 || failed.length > 0;
    if (missing.length === 0 && !hasOutcome)
        return null;
    return (React.createElement("div", { className: "service-titan-settings__section" },
        missing.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement(Text, { variant: "small", as: "h3" }, t('missing_heading')),
            React.createElement(Text, { variant: "body", as: "p" }, t('missing_hint')),
            React.createElement("ul", { className: "service-titan-settings__plain-list" }, missing.map((name) => (React.createElement("li", { key: name },
                React.createElement(Checkbox, { id: `service-titan-missing-${name}`, label: name, checked: selected.includes(name), disabled: submitting, onChange: (checked) => toggle(name, checked) }))))),
            React.createElement(Button, { variant: "secondary", disabled: submitting || selected.length === 0 || selected.length > CREATE_EQUIPMENT_TYPES_MAX, onClick: () => {
                    void submit();
                } }, submitting ? t('creating') : t('create_in_st')),
            selected.length === 0 && (React.createElement(Text, { variant: "x-small", as: "p", className: "service-titan-settings__muted" }, t('create_hint'))))),
        created.length > 0 && (React.createElement(InlineNotification, { variant: "success" }, t('created_list', { names: created.join(', ') }))),
        skipped.length > 0 && (React.createElement(InlineNotification, { variant: "info" }, t('skipped_list', { names: skipped.join(', ') }))),
        failed.map((row) => (React.createElement(InlineNotification, { key: row.name, variant: "error" }, t('failed_row', { name: row.name, message: row.message })))),
        discoveryNotApplied && React.createElement(InlineNotification, { variant: "warning" }, t('discovery_not_applied')),
        error && React.createElement(InlineNotification, { variant: "error" }, error)));
}
export default MissingEquipmentTypes;
